import React, { useState, useEffect } from 'react';
import { Zap, Shield, Users, Target, TrendingUp, Activity, Star, Rocket, CheckCircle } from 'lucide-react';

interface AboutProps {
  onLaunchPlatform?: () => void;
}

const About: React.FC<AboutProps> = ({ onLaunchPlatform }) => {
  const [activeTab, setActiveTab] = useState(0);

  // EKKO Logo URL
  const EKKO_LOGO = "https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png";

  const stats = [
    { value: "99.9%", label: "UPTIME", desc: "Reliable platform availability", icon: <Shield className="h-6 w-6" /> },
    { value: "<100ms", label: "LATENCY", desc: "Lightning-fast execution", icon: <Zap className="h-6 w-6" /> },
    { value: "2.5K+", label: "TRADERS", desc: "Active community", icon: <Users className="h-6 w-6" /> },
    { value: "24/7", label: "SUPPORT", desc: "Always here to help", icon: <Star className="h-6 w-6" /> }
  ];

  const features = [
    {
      title: "NON-CUSTODIAL SECURITY",
      desc: "Your assets remain in your control. We never store your private keys or have access to your funds.",
      icon: <Shield className="h-6 w-6" />,
      highlights: ["Private keys never leave your wallet", "Audited smart contracts", "Multi-sig protection"]
    },
    {
      title: "LIGHTNING FAST EXECUTION",
      desc: "Execute trades instantly with minimal slippage thanks to our optimized routing and deep liquidity pools.",
      icon: <Zap className="h-6 w-6" />,
      highlights: ["Sub-100ms trade execution", "Optimized routing algorithms", "MEV protection"]
    },
    {
      title: "ADVANCED AUTOMATION",
      desc: "Professional-grade trading tools, real-time analytics, and customizable automation features.",
      icon: <Target className="h-6 w-6" />,
      highlights: ["Custom trading bots", "Real-time market analysis", "Risk management tools"]
    },
    {
      title: "COMMUNITY DRIVEN",
      desc: "Join a thriving community of traders and developers building the future of DeFi.",
      icon: <Users className="h-6 w-6" />,
      highlights: ["Active Discord community", "Strategy sharing", "Educational resources"]
    }
  ];

  return (
    <div className="relative overflow-hidden bg-white min-h-screen">
      {/* Background architectural elements */}
      <div className="bg-orbs"></div>
      <div className="vertical-lines"></div>

      {/* Hero Section */}
      <section className="section corner-crosses py-16 md:py-24 lg:py-32 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-6xl px-4">
          <div className="max-w-4xl mx-auto text-center relative z-10">
              
            {/* EKKO Logo Badge */}
            <div className="inline-flex items-center justify-center p-3 glassmorphism backdrop-blur-xl border border-green-200/50 mb-8 group hover:shadow-[0_0_15px_rgba(80,220,160,0.3)] transition-all duration-300">
              <img 
                src={EKKO_LOGO}
                alt="EKKO" 
                className="h-8 w-8 mr-3 group-hover:scale-110 transition-transform" 
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors uppercase tracking-wider">
                ABOUT EKKO
              </span>
              </div>

            <h1 className="esports-title mb-8 relative">
              Why Choose <span className="text-green-500">EKKO</span>?
                </h1>
                
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              Built by traders for traders, EKKO v2 represents the next evolution in decentralized trading. 
              Our platform combines professional-grade tools with unmatched performance on Solana.
                </p>

            <div className="flex flex-wrap justify-center gap-4">
                  <button 
                    onClick={onLaunchPlatform}
                className="btn btn-primary btn-lg group relative overflow-hidden"
                  >
                <Rocket className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                <span>LAUNCH PLATFORM</span>
                  </button>
                  
              <button className="btn btn-outline btn-lg group">
                <span className="mr-2 group-hover:mr-3 transition-all">LEARN MORE</span>
                  </button>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Stats Section */}
      <section className="section corner-crosses py-16 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex items-center mb-12">
            <div className="cross-decoration w-4 h-4 mr-4"></div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">PLATFORM STATISTICS</h2>
              <p className="text-gray-600">Real-time performance metrics</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="arch-card glassmorphism p-8 text-center group hover:scale-105 transition-all duration-300">
                <div className="cross-decoration absolute top-4 left-4 w-3 h-3"></div>
                
                <div className="h-16 w-16 rounded-xl bg-green-100/50 backdrop-blur-xl border border-green-200/50 flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <div className="text-green-600">
                    {stat.icon}
                  </div>
                </div>

                <div className="stat-value text-3xl font-black text-gray-900 mb-2">
                    {stat.value}
                  </div>
                <div className="stat-label text-sm font-medium text-gray-600 uppercase tracking-wide mb-2">
                  {stat.label}
                </div>
                <p className="text-gray-500 text-sm">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="section corner-crosses py-16 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex items-center mb-12">
            <div className="cross-decoration w-4 h-4 mr-4"></div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">CORE FEATURES</h2>
              <p className="text-gray-600">What makes EKKO v2 the ultimate trading platform</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Interactive Feature Display */}
            <div className="relative">
              <div className="arch-card glassmorphism p-8 group">
                <div className="cross-decoration absolute top-4 right-4 w-3 h-3"></div>
                
                <div className="space-y-6">
                    {[
                      { title: "Non-Custodial Security", progress: 100 },
                      { title: "Lightning Fast Execution", progress: 95 },
                      { title: "Advanced Trading Tools", progress: 90 },
                      { title: "Community Governance", progress: 85 }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center space-x-4 group-hover:translate-x-2 transition-transform duration-500" style={{ transitionDelay: `${index * 100}ms` }}>
                      <div className="w-12 h-12 bg-green-100/50 rounded-xl flex items-center justify-center transform group-hover:rotate-180 transition-transform duration-700 border border-green-200/50">
                        <div className="w-6 h-6 bg-green-500 rounded-md"></div>
                        </div>
                        <div className="flex-1">
                        <div className="h-2 bg-gray-200/50 rounded-full mb-2 overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full transition-all duration-1000" 
                            style={{ width: `${item.progress}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">{item.title}</div>
                      </div>
                      <div className="text-2xl font-bold text-green-600 transform group-hover:scale-125 transition-transform duration-500">
                          {item.progress}%
                        </div>
                      </div>
                    ))}
                  </div>
              </div>
            </div>

            {/* Feature List */}
            <div className="space-y-8">
              {features.map((feature, index) => (
                <div key={index} className="arch-card glassmorphism p-6 group hover:scale-105 transition-all duration-300">
                  <div className="cross-decoration absolute top-3 right-3 w-2 h-2"></div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100/50 backdrop-blur-xl border border-green-200/50 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <div className="text-green-600">
                    {feature.icon}
                      </div>
                  </div>
                  
                  <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                      {feature.title}
                    </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                      {feature.desc}
                    </p>
                    
                      <ul className="space-y-2">
                        {feature.highlights.map((highlight, i) => (
                          <li key={i} className="flex items-center text-sm text-gray-500">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                            {highlight}
                          </li>
                      ))}
                    </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="section corner-crosses py-16 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-4xl px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-2 glassmorphism backdrop-blur-xl border border-green-200/50 mb-8">
              <Users className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm font-medium text-gray-700 uppercase tracking-wide">ACTIVE COMMUNITY</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Join 2,500+ Traders</h2>
            <p className="text-xl text-gray-600">Building the future of DeFi trading together</p>
          </div>

          <div className="relative">
            <div className="arch-card glassmorphism p-12 text-center">
              <div className="cross-decoration absolute top-6 left-6 w-4 h-4"></div>
              <div className="cross-decoration absolute top-6 right-6 w-4 h-4"></div>
              <div className="cross-decoration absolute bottom-6 left-6 w-4 h-4"></div>
              <div className="cross-decoration absolute bottom-6 right-6 w-4 h-4"></div>
              
              <div className="w-32 h-32 bg-green-100/50 rounded-full mx-auto mb-8 flex items-center justify-center border border-green-200/50">
                <div className="text-4xl font-black text-green-600">2.5K+</div>
            </div>

              <div className="inline-block bg-green-100/50 rounded-full px-8 py-4 border border-green-200/50">
                <span className="text-green-600 font-bold text-xl">Active Community Members</span>
                <div className="text-gray-500 text-sm mt-1">(Accounts created since V1 launch)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section corner-crosses py-16 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-4xl px-4">
          <div className="glassmorphism p-12 text-center relative overflow-hidden">
            <div className="cross-decoration absolute top-6 left-6 w-4 h-4"></div>
            <div className="cross-decoration absolute top-6 right-6 w-4 h-4"></div>
            <div className="cross-decoration absolute bottom-6 left-6 w-4 h-4"></div>
            <div className="cross-decoration absolute bottom-6 right-6 w-4 h-4"></div>

              <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black mb-6 text-gradient">
                  Ready to Get Started?
                </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Experience the full range of EKKO v2 services and take your trading to the next level.
                </p>
                
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={onLaunchPlatform}
                  className="btn btn-primary btn-lg group relative overflow-hidden"
                  >
                  <span className="relative z-10 flex items-center">
                    <Rocket className="h-5 w-5 mr-2" />
                    LAUNCH PLATFORM
                  </span>
                  </button>
                  
                <button className="btn btn-outline btn-lg group">
                  <span className="mr-2 group-hover:mr-3 transition-all">LEARN MORE</span>
                  </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 